import * as React from "react";
import { ChevronRight, File, Folder, Loader2, AlertCircle, RefreshCw, Play, Wrench, List, MessageCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarFooter,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarRail,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { useFlows, type FlowTreeNode } from "@flows/lib/use-flows";
import { useFlowSelection } from "@flows/state/FlowSelectionProvider";
import { useWorkspace } from "@/context/workspace";
import { NavUser } from "./NavUser";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { flowTree, isLoading, error, refetch } = useFlows();
  const { active } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Sidebar {...props}>
      <SidebarContent>
        {/* Tools group */}
        <SidebarGroup>
          <SidebarGroupLabel>Tools</SidebarGroupLabel>
          {/* Key by workspace so the tree & open state reset on switch */}
          <SidebarGroupContent key={active?.id}
          >
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/flows"); }}
                  isActive={location.pathname.startsWith("/flows")}
                >
                  <List className="h-4 w-4" />
                  Flows
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/chat"); }}
                  isActive={location.pathname.startsWith("/chat")}
                >
                  <MessageCircle className="h-4 w-4" />
                  Chat
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/admin/users"); }}
                  isActive={location.pathname.startsWith("/admin/users")}
                >
                  <File className="h-4 w-4" />
                  Admin · Users
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={location.pathname.startsWith("/flows/tests")}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate("/flows/tests");
                  }}
                >
                  <Play className="h-4 w-4" />
                  Test Suite
                </SidebarMenuButton>
              </SidebarMenuItem>
              {(() => {
                const isProd = (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.NODE_ENV === 'production') ||
                  (typeof process !== 'undefined' && (process as any)?.env?.NODE_ENV === 'production');
                if (isProd) return null;
                return (
                  <>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={location.pathname === "/dev" || location.pathname.startsWith("/dev/")}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate("/dev");
                        }}
                      >
                        <Wrench className="h-4 w-4" />
                        DevTools
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={location.pathname.startsWith("/dev/api")}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate("/dev/api");
                        }}
                      >
                        <RefreshCw className="h-4 w-4" />
                        API Tester
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </>
                );
              })()}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>
      {/* Footer: consolidated control */}
      <SidebarFooter className="mt-auto">
        <div className="border-t px-2 py-2">
          <NavUser />
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

function FlowTree({ node, path }: { node: FlowTreeNode; path: string }) {
  const { selectedFlowId, isLoadingFlow } = useFlowSelection();
  const navigate = useNavigate();

  if (node.type === "file" && node.flow) {
    const isActive = selectedFlowId === node.flow.id;
    return (
      <SidebarMenuItem key={path}>
        <SidebarMenuButton
          title={`${node.flow.title} - ${node.flow.category}`}
          className="justify-start"
          isActive={isActive}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate(`/flows/${encodeURIComponent(node.flow!.id)}`);
          }}
          disabled={isLoadingFlow && !isActive}
        >
          <File className="h-4 w-4" />
          <span className="truncate">{node.name}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  if (node.type === "folder" && node.children) {
    return (
      <SidebarMenuItem key={path}>
        <Collapsible className="group/collapsible [&[data-state=open]>button>svg:first-child]:rotate-90" defaultOpen={node.name === "executors" || node.name === "patterns"}>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton>
              <ChevronRight className="h-4 w-4 transition-transform" />
              <Folder className="h-4 w-4" />
              <span className="truncate">{node.name}</span>
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {node.children.map((childNode) => (
                <FlowTree key={`${path}/${childNode.name}`} node={childNode} path={`${path}/${childNode.name}`} />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </Collapsible>
      </SidebarMenuItem>
    );
  }

  return null;
}
